import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import useAuthEntra from 'hooks/useAuthPharo';
import { LookupModel } from 'types/lookups';
import appConfig from 'appConfig';
import Loader from 'components/Loader';
import { ConfigContext } from 'contexts/ConfigContext';

interface UserContextType {
  userFullName: string | null;
  userName: string | null;
  userInitials: string | null;
  userRoles: string[] | null;
  isUserPM: boolean;                 // The "current" user (may be overridden)
  isUserPriviledged: boolean;        // The "current" user (may be overridden)
  isUserAssistantPM: boolean;        // The "current" user (may be overridden)
  isUserET: boolean;                 // The "current" user (may be overridden)
  isOpsUser: boolean;                // The "current" user (may be overridden)
  isAdmin: boolean;                  // Only signed-in users with ApplicationAdministration role (for impersonation permissions)
  isCurrentUserAdmin: boolean;       // Current user (potentially impersonated) has ApplicationAdministration role
  lookups: LookupModel | null;
  isUserContextLoading: boolean;
  allowedPMs: string[] | null;
  canViewAndEditAll: boolean;
  canViewAll: boolean;

  signedInUserName: string | null;
  signedInFullName: string | null;
  isUserOverridden: boolean;

  refreshUserContext: () => void;
}

const initialState: UserContextType = {
  userFullName: null,
  userName: null,
  userInitials: null,
  userRoles: null,
  isUserPM: false,
  isUserPriviledged: false,
  isUserET : false,
  isUserAssistantPM: false,
  isOpsUser: false,
  isAdmin: false,
  isCurrentUserAdmin: false,
  lookups: null,
  isUserContextLoading: false,
  allowedPMs: null,
  canViewAndEditAll: false,
  canViewAll: false,

  signedInUserName: null,
  signedInFullName: null,
  isUserOverridden: false,

  refreshUserContext: () => {},
};

const UserContext = createContext<UserContextType>(initialState);

const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userData, setUserData] = useState<UserContextType>(initialState);
  const { getCurrentToken, accessToken, user } = useAuthEntra();
  const configContext = useContext(ConfigContext);
  const [isInitialized, setIsInitialized] = useState(false);

  // Simple helper to decode a JWT without verifying it (sufficient for client-side claim extraction)
  const decodeJwt = (token: string | null) => {
    if (!token) return null;
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (err) {
      console.warn("Failed to decode JWT", err);
      return null;
    }
  };

  const fetchUserData = async () => {
    if (!accessToken) {
      setUserData((prev) => ({ ...prev, isUserContextLoading: false }));
      return;
    }
    
    // Wait for a valid token before proceeding
    let token = getCurrentToken();
    if (!token) {
      console.log(">>> Waiting for valid token before fetching user data - token is null");
      // Schedule a retry after a short delay in case token becomes available
      setTimeout(() => {
        if (getCurrentToken() && !isInitialized) {
          console.log(">>> Retrying user data fetch after delay");
          fetchUserData();
        }
      }, 1000); // Wait 1 second before retry
      return;
    }
    
    try {
      console.log(">>> Proceeding with user data fetch - token available");
      // 1) Extract user claims – prefer MSAL account if present, otherwise decode the JWT
      const claims: any = user?.idTokenClaims ?? decodeJwt(accessToken);
      const signedInFullName = user?.name ?? claims?.name ?? '[NO NAME PROVIDED]';
      const signedInUserName = claims?.upn
        ? claims.upn.split('@')[0]
        : claims?.preferred_username
        ? claims.preferred_username.split('@')[0]
        : '[NO USERNAME PROVIDED]';
      const userRoles: string[] = claims?.roles ?? [];
      // 2) Fetch the lookups data - use getCurrentToken for consistency with useApi
      const currentToken = getCurrentToken();
      const response = await axios.get(`${appConfig.api.orderApiUrl}/lookups`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      const lookups: LookupModel = response.data.data as LookupModel;
      // 1) By default, the real user from the token
      let currentUserName = signedInUserName;
      let currentUserFullName = signedInFullName;
      let currentUserInitials =
        lookups.pharoUsers.find((u) => u.userName === signedInUserName)?.initials ??
        '[NO INITIALS]';
      let isUserOverridden = false;
      // 2) Check if user is admin
      const isAdmin = lookups.pharoRoles.find((u) => u.userName === signedInUserName && u.roleType === 'ApplicationAdministration') ? true : false;
      // 3) Check query param (e.g. user=ds)
      const params = new URLSearchParams(window.location.search);
      const overrideInitialsFromParam = params.get('user');
      if(overrideInitialsFromParam)
      {
        configContext.onOrdersFiltersChange({ portfolioManager: '' });
      }
      // 4) If the user is admin & param is present, store it in sessionStorage
      if (isAdmin && overrideInitialsFromParam) {
        sessionStorage.setItem('overrideInitials', overrideInitialsFromParam);
      }
      // 5) Read the override from sessionStorage (if admin)
      let overrideInitials:string | null = null;
      if (isAdmin) {
        overrideInitials = sessionStorage.getItem('overrideInitials');
      }
      // 6) Attempt to override if we found `overrideInitials`
      if (isAdmin && overrideInitials !== null) {
        const overridePharoUser = lookups.pharoUsers.find(
          (pu) => pu.initials.toLowerCase() === overrideInitials!.toLowerCase()
        );
        if (overridePharoUser) {
          currentUserName = overridePharoUser.userName;
          currentUserFullName = overridePharoUser.firstName + ' ' + overridePharoUser.lastName;
          currentUserInitials = overridePharoUser.initials;
          isUserOverridden = true;
        }
      }
      // 7) Determine some of user's relevant roles
      const isUserPM = lookups.portfolioManagers.find((u) => u.initials === currentUserInitials) ? true : false;
      const privilegedRoles = ["ApplicationAdministration", "ExecutionTrader", "Trader", "TradingAssistant", "RiskCommitee", "Partner", "ChiefEconomist", "NoPortfolioDataRestriction"];
      const isUserPriviledged = lookups.pharoRoles.some((u) => u.userName === currentUserName && privilegedRoles.includes(u.roleType));
      const isUserAssistantPM = lookups.pharoRoles.find((u) => u.userName === currentUserName && u.roleType === 'AssistantPortfolioManager') ? true : false;
      const isUserET = lookups.pharoRoles.find((u) => u.userName === currentUserName && (u.roleType === 'ExecutionTrader' || u.roleType === 'Trader' || u.roleType === 'TradingAssistant')) ? true : false;
      // Check if current user (potentially impersonated) has admin role
      const isCurrentUserAdmin = lookups.pharoRoles.find((u) => u.userName === currentUserName && u.roleType === 'ApplicationAdministration') ? true : false;
      let allowedPMs = lookups.impersonationInfos.filter((u) => u.impersonatorUsername === currentUserName).map((u) => u.impersonateeInitials);
      if(isUserPM)
        allowedPMs.push(currentUserInitials);
      let canViewAndEditAll = isUserET;
      let canViewAll = lookups.pharoRoles.find((u) => allowedPMs.includes(u.userName) && (u.roleType === 'RiskCommittee' || u.roleType === 'Partner')) ? true : false;
      // 8) Update context state
      setUserData({
        userFullName: currentUserFullName,
        userName: currentUserName,
        userInitials: currentUserInitials,
        userRoles,
        isUserPM,
        isUserPriviledged,
        isUserET,
        isUserAssistantPM,          
        isOpsUser: lookups.pharoRoles.some((u) => u.userName === currentUserName && u.roleType === 'Operations'),
        isAdmin,
        isCurrentUserAdmin,
        lookups,
        isUserContextLoading: false,
        allowedPMs,
        canViewAndEditAll,
        canViewAll,
        signedInUserName,
        signedInFullName,
        isUserOverridden,
        refreshUserContext: () => {
          setIsInitialized(false);
          fetchUserData();
        }
      });
      setIsInitialized(true);
    } catch (error) {
      console.error('Error fetching user data or lookups:', error);
      setUserData((prev) => ({ ...prev, isUserContextLoading: false }));
    }
  };

  const refreshUserContext = () => {
    setIsInitialized(false);
    fetchUserData();
  };

  useEffect(() => {
    if (!isInitialized && accessToken) {
      setUserData((prev) => ({ ...prev, isUserContextLoading: true }));
      fetchUserData();
    }
    // eslint-disable-next-line
  }, [user, accessToken, isInitialized]);

  // Additional effect to retry fetchUserData when token becomes available
  useEffect(() => {
    if (accessToken && !isInitialized) {
      const token = getCurrentToken();
      if (token && !userData.lookups) {
        // Token is available but we haven't fetched user data yet, try again
        console.log(">>> Token now available, retrying user data fetch");
        setUserData((prev) => ({ ...prev, isUserContextLoading: true }));
        fetchUserData();
      }
    }
  }, [accessToken, getCurrentToken, isInitialized, userData.lookups]);

  // If still loading user context, show loader
  if (userData.isUserContextLoading) {
    return <Loader />;
  }

  return <UserContext.Provider value={{ ...userData, refreshUserContext }}>{children}</UserContext.Provider>;
};

export { UserProvider, UserContext };
